import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { CalendarCheck, CalendarRange, CalendarClock, CheckCircle2, ArrowRight } from 'lucide-react';

/**
 * Las cifras de la Agenda, encima del calendario.
 *
 * Antes esto era la pantalla «Inicio». Tenía cuatro botones —Hoy, Semana,
 * Nueva cita e Ir al CRM— y los cuatro llevaban a un sitio al que ya se podía
 * llegar sin pasar por ahí: «Hoy» y «Semana» al calendario (que además tiene
 * su propio botón «Hoy»), «Nueva cita» al mismo botón que el calendario ya
 * trae, y «Ir al CRM» al menú principal. Ninguno se copió aquí.
 *
 * Lo que sí valía la pena eran los números y la próxima cita, y ahora se ven
 * sin salir del calendario.
 *
 * Ninguna cifra es clicable: estando en el calendario, todas describen lo que
 * ya tienes delante. Un número que parece botón y no hace nada molesta más
 * que un número que se ve como lo que es.
 */

const Cifra = ({ icono: Icono, valor, etiqueta, color }) => (
  <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-[#E2E8F0] bg-white whitespace-nowrap">
    <Icono className={`w-4 h-4 ${color} flex-shrink-0`} />
    <span className="text-base font-bold text-[#0F172A] tabular-nums leading-none">{valor}</span>
    <span className="text-[11px] text-[#64748B] leading-tight">{etiqueta}</span>
  </div>
);

export default function AgendaResumen({ onSinCitas }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    // De fondo: el calendario es lo que la persona vino a ver, y unos
    // contadores no pueden taparlo con la pantalla de carga.
    api.get('/tenant/agenda/status', { silencioso: true })
      .then(r => {
        setStats(r.data);
        onSinCitas?.((r.data?.scheduled_count ?? 0) === 0);
      })
      .catch(() => setStats(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!stats) return null;

  const etiquetas = stats.vertical_labels || {};
  const cita = (etiquetas.appointment || 'Cita').toLowerCase();
  const proxima = stats.upcoming_next;

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="agenda-resumen">
      <Cifra icono={CalendarCheck} valor={stats.today_count ?? 0} etiqueta="hoy"
        color="text-blue-500" />
      <Cifra icono={CalendarRange} valor={stats.week_count ?? 0} etiqueta="esta semana"
        color="text-emerald-500" />
      <Cifra icono={CalendarClock} valor={stats.scheduled_count ?? 0} etiqueta={`${cita}s agendadas`}
        color="text-[#64748B]" />
      <Cifra icono={CheckCircle2} valor={stats.completed_today_count ?? 0} etiqueta="realizadas hoy"
        color="text-purple-500" />

      {proxima && (
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-blue-200
                        bg-blue-50 min-w-0 max-w-full" data-testid="agenda-resumen-proxima">
          <ArrowRight className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
          <span className="text-[11px] text-blue-700 font-medium flex-shrink-0">Sigue:</span>
          <span className="text-[11px] text-[#0F172A] truncate">
            {proxima.lead_name || proxima.title}
            {proxima.starts_at && ` · ${new Date(proxima.starts_at).toLocaleString([], {
              day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
            })}`}
          </span>
        </div>
      )}
    </div>
  );
}
